import React, { createContext, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PageInfo } from 'Types/dealGPT';

import { selectFolders } from '../../redux/reducers/folderReducer';
import { AnalyticsEvent, useAnalytics } from './AnalyticsProvider';
/**
 * Context type defining available NavigationIndex operations.
 */
interface NavigationIndexContextType {
  stepIndex: number;
  pages: PageInfo[];
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToPage: (index: number) => void;
  setPageNames: React.Dispatch<React.SetStateAction<PageInfo[]>>;
}

const NavigationIndexContext = createContext<NavigationIndexContextType>({
  stepIndex: 0,
  pages: [],
  goToNextPage: () => {},
  goToPreviousPage: () => {},
  goToPage: () => {},
  setPageNames: () => {},
});

// Provider Component
export function NavigationIndexProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { currentFolder } = useSelector(selectFolders);
  const [pages, setPageNames] = useState<PageInfo[]>([]);
  const [stepIndex, setCurrentIndex] = useState(0);

  const goToNextPage = useCallback(() => {
    if (stepIndex >= pages.length - 1) {
      analytics.event(AnalyticsEvent.WM_IA_A_FINISH_CLICK);
      navigate(`/folder/${currentFolder?.id}/analysis`);
    }
    setCurrentIndex((prevIndex) => (prevIndex < pages.length - 1 ? prevIndex + 1 : prevIndex));
  }, [pages, stepIndex, currentFolder, navigate, analytics]);

  const goToPreviousPage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  }, []);

  const goToPage = useCallback(
    (index: number) => {
      if (index >= 0 && index < pages.length) {
        setCurrentIndex(index);
      }
    },
    [pages]
  );

  return (
    <NavigationIndexContext.Provider
      value={{ stepIndex, pages, goToNextPage, goToPreviousPage, goToPage, setPageNames }}
    >
      {children}
    </NavigationIndexContext.Provider>
  );
}

// Custom hook to use the NavigationContext
export const useNavigationIndex = () => {
  return useContext(NavigationIndexContext);
};
